import React, { useEffect } from 'react';
import { 
  FaGraduationCap, 
  FaShieldAlt,
  FaBook,
  FaRobot,
  FaStar,
  FaGem,
  FaBrain,
  FaHandshake,
  FaLock
} from 'react-icons/fa';
import { 
  BiAnalyse,
  BiLineChart
} from 'react-icons/bi';
import '../styles/Documentation.css';

const Documentation = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="documentation">
      <div className="doc-container">
        <header className="doc-header">
          <div className="badge">Documentation</div>
          <h1>The Firm Protocols</h1>
          <p>Your AI-Powered Crypto Trading Assistant</p>
        </header>

        <div className="doc-content">
          <section className="doc-section">
            <h2>Abstract</h2>
            <p>The Firm Protocols is an AI-powered Telegram Bot designed to assist and educate cryptocurrency beginners. By leveraging artificial intelligence, we provide users with real-time information on new token launches, risk analysis, and comprehensive guides to buying and selling cryptocurrencies.</p>
            <p>Our platform offers advanced safety assessments for crypto projects, helping users identify secure and promising opportunities in the dynamic crypto market.</p>
          </section>

          <section className="doc-section">
            <h2>Introduction</h2>
            <p>Cryptocurrency markets are notoriously volatile and complex, posing significant challenges for beginners. Understanding token launches, assessing risks, and executing trades can be overwhelming for newcomers. Despite the wealth of information available, it is often scattered, technical, and difficult to interpret.</p>
            
            <div className="doc-grid">
              <div className="doc-card">
                <div className="card-icon"><FaGraduationCap /></div>
                <h4>Educational Assistance</h4>
                <p>Guiding users from zero to hero in crypto knowledge with comprehensive tutorials and resources.</p>
              </div>
              <div className="doc-card">
                <div className="card-icon"><BiAnalyse /></div>
                <h4>Risk Analysis</h4>
                <p>Helping users make informed decisions with AI-driven insights and market analysis.</p>
              </div>
              <div className="doc-card">
                <div className="card-icon"><FaShieldAlt /></div>
                <h4>Safety Assessments</h4>
                <p>Identifying safe projects through comprehensive evaluations and continuous monitoring.</p>
              </div>
            </div>
          </section>

          <section className="doc-section">
            <h2>Core Features</h2>
            <div className="doc-grid">
              <div className="doc-card feature-card">
                <div className="card-icon"><BiLineChart /></div>
                <h4>Real-Time Token Information</h4>
                <ul className="doc-list">
                  <li>New token launches with essential data</li>
                  <li>Market cap and ticker information</li>
                  <li>Launch platform details</li>
                  <li>Risk analysis based on liquidity and volume</li>
                </ul>
              </div>

              <div className="doc-card feature-card">
                <div className="card-icon"><FaBook /></div>
                <h4>Beginner's Guide</h4>
                <ul className="doc-list">
                  <li>Step-by-step wallet setup tutorials</li>
                  <li>Comprehensive crypto glossary</li>
                  <li>Trading and investing best practices</li>
                  <li>Security tips and guidelines</li>
                </ul>
              </div>

              <div className="doc-card feature-card">
                <div className="card-icon"><FaRobot /></div>
                <h4>AI Safety Analysis</h4>
                <ul className="doc-list">
                  <li>Market cap trend analysis</li>
                  <li>Team verification and credibility checks</li>
                  <li>Social media presence validation</li>
                  <li>Marketing activity assessment</li>
                </ul>
              </div>

              <div className="doc-card feature-card">
                <div className="card-icon"><FaStar /></div>
                <h4>Project Recommendations</h4>
                <ul className="doc-list">
                  <li>AI-curated promising projects</li>
                  <li>Growth and engagement metrics</li>
                  <li>Team credential verification</li>
                  <li>Communication channel analysis</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="doc-section">
            <h2>Technology Overview</h2>
            <div className="doc-grid">
              <div className="doc-card tech-card">
                <div className="card-icon"><FaBrain /></div>
                <h4>AI Integration</h4>
                <ul className="doc-list">
                  <li>Advanced machine learning models</li>
                  <li>Natural Language Processing (NLP)</li>
                  <li>Real-time data analysis</li>
                </ul>
              </div>
              <div className="doc-card tech-card">
                <div className="card-icon"><FaHandshake /></div>
                <h4>Bot Framework</h4>
                <ul className="doc-list">
                  <li>Intuitive command interface</li>
                  <li>Real-time notifications</li>
                  <li>Seamless user experience</li>
                </ul>
              </div>
              <div className="doc-card tech-card">
                <div className="card-icon"><FaLock /></div>
                <h4>Security</h4>
                <ul className="doc-list">
                  <li>Data encryption</li>
                  <li>GDPR compliance</li>
                  <li>Automated scam detection</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="doc-section">
            <h2>Pricing Plans</h2>
            <div className="doc-grid">
              <div className="doc-card pricing-card">
                <div className="card-icon"><FaStar /></div>
                <h4>Free Tier</h4>
                <ul className="doc-list">
                  <li>Basic token alerts</li>
                  <li>Beginner guides</li>
                  <li>Limited analysis</li>
                </ul>
              </div>
              <div className="doc-card pricing-card premium">
                <div className="card-icon"><FaGem /></div>
                <h4>Premium Tier</h4>
                <ul className="doc-list">
                  <li>Advanced project analysis</li>
                  <li>Exclusive recommendations</li>
                  <li>Priority support</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="doc-section conclusion">
            <h2>Get Started</h2>
            <p>The Firm Protocols is revolutionizing the crypto onboarding experience for beginners. By combining AI-powered insights with a user-friendly Telegram interface, we simplify crypto trading, enhance decision-making, and foster safer participation in the cryptocurrency market.</p>
            <div className="cta-container">
              <a href="https://t.me/TheFirmAIProtocols_bot" target="_blank" rel="noopener noreferrer" className="cta-button">
                Launch Bot
              </a>
            </div>
          </section>
        </div>
      </div>
      <div className="doc-overlay"></div>
      <div className="doc-particles"></div>
    </div>
  );
};

export default Documentation; 